import { type ReactNode, useState } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { ClientOption } from '@/types';

import { typedFields } from '@/components/Form';
import { FieldError } from '@/components/ui/field';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { toFormString } from '@/helpers';

import { ClientAutoComplete } from '../ClientAutoComplete';

type ClientOrGuestValues = {
  client_id: string;
  guest_info: string;
};

type ClientOrGuestKind = 'client' | 'guest';

type ClientOrGuestFieldProps = {
  clientsSearchUrl: string;
  queryKey: string[];
  client: ClientOption | null;
  onClientChange: (client: ClientOption | null) => void;
  children?: ReactNode;
};

const formFields = typedFields<ClientOrGuestValues>();

export const ClientOrGuestField = (props: ClientOrGuestFieldProps) => {
  const {
    clientsSearchUrl,
    queryKey,
    client,
    onClientChange: propOnClientChange,
    children,
  } = props;
  const { t } = useTranslation();
  const form = useFormContext<ClientOrGuestValues>();
  const { errors, isSubmitted } = useFormState({
    control: form.control,
    name: 'guest_info',
  });

  const [kind, setKind] = useState<ClientOrGuestKind>('client');

  const onKindChange = (value: string) => {
    setKind(value as ClientOrGuestKind);
    propOnClientChange(null);
    form.setValue('client_id', '');
    form.setValue('guest_info', '');
  };

  const onClientChange = (value: ClientOption | null) => {
    propOnClientChange(value);
    form.setValue('client_id', toFormString(value?.id), {
      shouldValidate: isSubmitted,
    });
  };

  return (
    <>
      <Tabs
        value={kind}
        onValueChange={onKindChange}
      >
        <TabsList variant="segmented">
          <TabsTrigger value="client">{t('Existing client')}</TabsTrigger>
          <TabsTrigger value="guest">{t('Guest')}</TabsTrigger>
        </TabsList>
      </Tabs>

      {kind === 'client' && (
        <>
          <div className="grid gap-2">
            <ClientAutoComplete
              fetchUrl={clientsSearchUrl}
              queryKey={queryKey}
              label={t('Client')}
              value={client}
              onChange={onClientChange}
            />
            {!client && <FieldError errors={[errors.guest_info]} />}
          </div>

          {children}
        </>
      )}

      {kind === 'guest' && (
        <formFields.Input
          name="guest_info"
          label={t('Guest name / phone')}
          isRequired
        />
      )}
    </>
  );
};
